// src/pages/Wallet/Wallet.js - Updated with NFT Send Support
import React, { useContext, useRef, useState, useEffect } from 'react';
import { WalletContext } from '../../contexts/WalletContext';
import { NetworkContext } from '../../contexts/NetworkContext';
import { AuthContext } from '../../contexts/AuthContext';
import { DAppContext } from '../../contexts/DAppContext';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';

// Custom hooks
import { useWalletData } from './hooks/useWalletData';
import { useNFTData } from './hooks/useNFTData';
import { usePriceData } from './hooks/usePriceData';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { useTokenManagement } from './hooks/useTokenManagement';
import { useTransactions } from './hooks/useTransactions';
import { useModalState } from './hooks/useModalState';
import { useNetworkManagement } from './hooks/useNetworkManagement';
import { usePortfolioCache } from './hooks/usePortfolioCache';

// Components
import WalletHeader from './components/WalletHeader';
import BalanceDisplay from './components/BalanceDisplay';
import ActionButtons from './components/ActionButtons';
import TabSelector from './components/TabSelector';
import TokenList from './components/TokenList';
import NFTList from './components/NFTList';
import PullToRefresh from './components/PullToRefresh';
import TransactionNotificationWrapper from '../../components/TransactionNotification/TransactionNotificationWrapper';
import RequestModalController from '../../components/RequestModalController';
import ConnectionSuccessNotificationWrapper from '../../components/ConnectionSuccessNotification/ConnectionSuccessNotificationWrapper';

// Modals
import NetworkModal from './modals/NetworkModal';
import SendModal from './modals/SendModal';
import SendNFTModal from './modals/SendNFTModal'; // NEW: Import SendNFTModal
import ReceiveModal from './modals/ReceiveModal';
import TokenManagementModal from './modals/TokenManagementModal';
import WalletSelectorModal from './modals/WalletSelectorModal';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import TokenInfoModal from './modals/TokenInfoModal';
import NFTInfoModal from './modals/NFTInfoModal';
import QRScannerModal from './modals/QRScannerModal';
import PortfolioChartModal from './modals/PortfolioChartModal';

// Utils
import { calculatePortfolioValue, calculatePortfolioChange, formatPortfolioValue, createTokenBalancesMap } from './utils/tokenUtils';

// Styles
import './Wallet.css';

const Wallet = () => {
  const { t } = useTranslation();
  
  // Context
  const { 
    userWallets, 
    setUserWallets, 
    currentWallet, 
    setCurrentWallet, 
    createWallet, 
    importWallet,
    hasStoredWallets,
    isInitialized,
    provider
  } = useContext(WalletContext);

  const { isAuthenticated } = useContext(AuthContext);
  
  const { currentChain, switchNetwork } = useContext(NetworkContext);
  
  const { connectWithWalletConnectUri } = useContext(DAppContext);

  // Track previous chain for detecting chain changes
  const prevChainRef = useRef(null);
  
  // State for network changes
  const [networkChangeCounter, setNetworkChangeCounter] = useState(0);
  
  // Tab state for switching between Tokens and NFTs
  const [activeTab, setActiveTab] = useState('tokens');
  
  const { 
    hiddenNetworks,
    toggleNetworkVisibility, 
    restoreAllNetworks 
  } = useNetworkManagement();
  
  // Notification refs
  const notificationRef = useRef(null);
  const connectionSuccessRef = useRef(null);

  // State for loading/no wallet message
  const [isLoadingWallet, setIsLoadingWallet] = useState(true);

  // Get wallet data
  const { 
    balance, 
    tokens, 
    customTokens,
    isRefreshing: isDataRefreshing, 
    reloadCustomTokens,
    refreshAll, 
    refreshBalance, 
    refreshTokens,
    refreshCustomTokens
  } = useWalletData();

  // Get NFT data with caching
  const {
    nfts,
    isRefreshing: isNFTRefreshing,
    error: nftError,
    fromCache: nftsFromCache,
    refreshNFTs,
    refreshAll: refreshAllNFTs,
    forceRefresh: forceRefreshNFTs,
    getNFTById,
    getNFTsByCollection,
    getCollections,
    getCacheStatus
  } = useNFTData();

  const { 
    hiddenTokenAddresses,
    currentChainHiddenTokens,
    handleToggleTokenVisibility,
    handleAddCustomToken: baseHandleAddCustomToken,
    handleRemoveCustomToken: baseHandleRemoveCustomToken
  } = useTokenManagement(refreshCustomTokens);
  
  const { 
    nativePrice, 
    nativePriceChange,
    tokenPrices,
    tokenLogos,
    tokenPriceChanges
  } = usePriceData(
    [
      ...tokens, 
      ...(customTokens[currentChain?.key] || [])
    ],
    currentChainHiddenTokens
  );
  
  // Calculate total portfolio value and percentage change
  const portfolioTotalValue = calculatePortfolioValue(
    balance, 
    nativePrice, 
    [...tokens, ...(customTokens[currentChain?.key] || [])],
    tokenPrices
  );
  
  // Calculate 24h portfolio change percentage
  const portfolioChangePercentage = calculatePortfolioChange(
    balance,
    nativePrice,
    nativePriceChange || 0,
    [...tokens, ...(customTokens[currentChain?.key] || [])],
    tokenPrices,
    tokenPriceChanges
  );
  
  // Create token balances map for SendModal
  const tokenBalancesMap = createTokenBalancesMap(
    balance, 
    [...tokens, ...(customTokens[currentChain?.key] || [])]
  );
  
  // Portfolio chart state and hook
  const [showPortfolioChartModal, setShowPortfolioChartModal] = useState(false);
  const [portfolioChartData, setPortfolioChartData] = useState([]);
  const { cachePortfolioValue, getCurrentWalletHistory } = usePortfolioCache();
  
  // Create combined refresh function for both tokens and NFTs
  const refreshAllData = async (forceRefresh = false) => {
    if (forceRefresh) {
      // Force refresh bypasses all caches
      await Promise.all([
        refreshAll(),
        forceRefreshNFTs()
      ]);
    } else {
      // Normal refresh uses caches when available
      await Promise.all([
        refreshAll(),
        refreshAllNFTs()
      ]);
    }
  };
  
  // Pull-to-refresh functionality (force refresh on pull)
  const { 
    scrollContainerRef, 
    pullDistance, 
    isRefreshing: isPullRefreshing, 
    refreshThreshold, 
    handleTouchStart, 
    handleTouchMove, 
    handleTouchEnd,
    refreshData
  } = usePullToRefresh(() => refreshAllData(true)); // Force refresh on pull-to-refresh
  
  // Transaction handling
const { 
  selectedToken,
  setSelectedToken,
  selectedNFT, // NEW: Get NFT state from hook
  setSelectedNFT, // NEW: Get NFT setter from hook
  confirmationDetails,
  setConfirmationDetails,
  txnHash,
  setTxnHash,
  dynamicGasPrice,
  prepareTransaction,
  prepareNFTTransaction, // NEW: Get NFT prepare function
  executeTransaction,
  executeNFTTransaction, // NEW: Get NFT execute function
  handleGasSettingChange,
  getReadableGasPrice,
  fetchGasPrice
} = useTransactions(refreshBalance, refreshTokens);
  
  // Modal state management - NOW INCLUDES SendNFTModal
  const { 
    showNetworkModal,
    showWalletModal,
    showSendModal,
    showSendNFTModal, // NEW: Added SendNFTModal state
    showReceiveModal,
    showTokenManagementModal,
    isConfirmationModalOpen,
    toggleNetworkModal,
    toggleWalletModal,
    toggleSendModal,
    toggleSendNFTModal, // NEW: Added SendNFTModal toggle
    toggleReceiveModal,
    toggleTokenManagementModal,
    toggleConfirmationModal
  } = useModalState();
  
  // Additional modal states for NFTs and tokens
  const [showNFTInfoModal, setShowNFTInfoModal] = useState(false);
  
  // QR scanner state
  const [showQRScannerModal, setShowQRScannerModal] = useState(false);
  const [showTokenInfoModal, setShowTokenInfoModal] = useState(false);

  // Cache portfolio value when it changes
  useEffect(() => {
    if (portfolioTotalValue > 0 && !isDataRefreshing) {
      // Only cache when portfolio value is positive and data is not refreshing
      cachePortfolioValue(portfolioTotalValue);
    }
  }, [portfolioTotalValue, cachePortfolioValue, isDataRefreshing]);

  // Enhanced network switching with forced refresh
  const handleSwitchNetwork = (chainKey) => {
    switchNetwork(chainKey);
    toggleNetworkModal(false);
    
    // Force a refresh by incrementing the counter
    setNetworkChangeCounter(prev => prev + 1);
  };
  
  // Detect network changes and force a refresh
  useEffect(() => {
    if (prevChainRef.current && 
        prevChainRef.current !== currentChain?.key && 
        currentWallet && 
        provider) {
      console.log(`Network changed from ${prevChainRef.current} to ${currentChain?.key}. Refreshing wallet data...`);
      
      // Perform a full refresh after a small delay
      setTimeout(() => {
        refreshAllData();
        fetchGasPrice();
      }, 500);
    }
    
    // Update the ref with current chain
    prevChainRef.current = currentChain?.key;
  }, [currentChain, currentWallet, provider, fetchGasPrice]);
  
  // Force a refresh when the network change counter updates
  useEffect(() => {
    if (networkChangeCounter > 0 && currentWallet && provider) {
      console.log("Network change detected. Forcing refresh...");
      
      // Add a small delay to ensure network change is complete
      const timer = setTimeout(() => {
        refreshAllData();
        refreshBalance(); // Explicitly refresh native balance
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [networkChangeCounter, currentWallet, provider, refreshBalance]);

  // Make the connection success notification ref available globally
  useEffect(() => {
    if (connectionSuccessRef.current && isInitialized) {
      if (connectWithWalletConnectUri && connectionSuccessRef.current) {
        window._connectionSuccessRef = connectionSuccessRef.current;
        console.log('Connection success notification ref registered globally');
      }
    }
  }, [isInitialized, connectWithWalletConnectUri]);

  // Enhanced token management functions
  const handleAddCustomToken = async (tokenInfo) => {
    if (!tokenInfo || !tokenInfo.address || !currentChain) return;
    
    const chainKey = currentChain.key;
    const lowercaseAddress = tokenInfo.address.toLowerCase();
    
    // First reload customTokens to ensure we have the latest data
    const currentCustomTokens = reloadCustomTokens();
    
    // Check if token exists in custom tokens for this chain
    const chainCustomTokens = currentCustomTokens[chainKey] || [];
    const existsInCustomTokens = chainCustomTokens.some(t => 
      t.token && t.token.address && 
      t.token.address.toLowerCase() === lowercaseAddress
    );
    
    // Check if token exists in auto-detected tokens
    const existsInAutoDetectedTokens = tokens.some(t => 
      t.token && t.token.address && 
      t.token.address.toLowerCase() === lowercaseAddress
    );
    
    // If token exists in either list
    if (existsInCustomTokens || existsInAutoDetectedTokens) {
      // If hidden, unhide it
      if (currentChainHiddenTokens.includes(lowercaseAddress)) {
        handleToggleTokenVisibility(tokenInfo.address);
        alert(t('wallet.tokenUnhidden', 'This token was hidden. It is now visible in your wallet.'));
      } else {
        alert(t('wallet.tokenAlreadyExists', 'This token is already in your wallet'));
      }
      return;
    }
    
    // Format the new token in the same structure as other tokens
    const newToken = {
      token: {
        type: "ERC-20",
        address: tokenInfo.address,
        name: tokenInfo.name,
        symbol: tokenInfo.symbol,
        decimals: tokenInfo.decimals,
        isCustom: true, // Mark as custom token
        chainKey: chainKey // Store which chain this token belongs to
      },
      value: tokenInfo.balance || "0"
    };
    
    try {
      // Update localStorage directly
      const updatedCustomTokens = { ...currentCustomTokens };
      if (!updatedCustomTokens[chainKey]) {
        updatedCustomTokens[chainKey] = [];
      }
      updatedCustomTokens[chainKey] = [...updatedCustomTokens[chainKey], newToken];
      
      localStorage.setItem('customTokens', JSON.stringify(updatedCustomTokens));
      
      // Call base handler from useTokenManagement
      await baseHandleAddCustomToken(tokenInfo);
      
      // Force reload and refresh tokens
      reloadCustomTokens();
      await refreshCustomTokens();
      
      // Show success message
      alert(t('wallet.tokenAdded', 'Token added successfully!'));
      
    } catch (error) {
      console.error("Error saving custom token:", error);
      alert(t('wallet.errorAddingToken', 'Error adding token. Please try again.'));
    }
  };
  
  const handleRemoveCustomToken = (tokenAddress) => {
    if (!tokenAddress || !currentChain) return;
    
    const chainKey = currentChain.key;
    const lowercaseAddress = tokenAddress.toLowerCase();
    
    // Confirm before removing
    const confirmRemove = window.confirm(t('wallet.confirmRemoveToken', 'Are you sure you want to remove this token from your wallet?'));
    if (!confirmRemove) return;
    
    try {
      // Update localStorage directly
      const updatedCustomTokens = { ...customTokens };
      if (updatedCustomTokens[chainKey]) {
        updatedCustomTokens[chainKey] = updatedCustomTokens[chainKey].filter(t => 
          !t.token || !t.token.address || 
          t.token.address.toLowerCase() !== lowercaseAddress
        );
        
        localStorage.setItem('customTokens', JSON.stringify(updatedCustomTokens));
      }
      
      // Call base handler
      baseHandleRemoveCustomToken(tokenAddress);
      
      // Force refresh to update UI
      refreshAll();
      
    } catch (error) {
      console.error("Error removing custom token:", error);
      alert(t('wallet.errorRemovingToken', 'Error removing token. Please try again.'));
    }
  };

  // Check wallet state after initialization
  useEffect(() => {
    if (isInitialized) {
      // Add a small delay to ensure wallet data is properly loaded
      const timer = setTimeout(() => {
        setIsLoadingWallet(false);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isInitialized, userWallets, currentWallet]);
  
  // Filter out hidden tokens
  const filteredTokens = [
    ...tokens, 
    ...(customTokens[currentChain?.key] || [])
  ].filter(t => 
    !(t.token && t.token.address && currentChainHiddenTokens.includes(t.token.address.toLowerCase()))
  );
  
  const handleSwitchWallet = (walletObj) => {
    setCurrentWallet(walletObj);
    toggleWalletModal(false);
  };
  
  const handleTokenClick = (tokenData) => {
    setSelectedToken(tokenData);
    setShowTokenInfoModal(true);
  };
  
  const handleNFTClick = (nftData) => {
    setSelectedNFT(nftData);
    setShowNFTInfoModal(true);
  };
  
  const handleSendFromInfo = (tokenData) => {
    setSelectedToken(tokenData);
    setSelectedNFT(null); // Clear NFT selection
    setShowTokenInfoModal(false);
    toggleSendModal(true);
  };

  // UPDATED: Handle NFT send from NFT info modal - now opens SendNFTModal
  const handleSendFromNFTInfo = (nftData) => {
    setSelectedNFT(nftData);
    setSelectedToken(null); // Clear token selection
    setShowNFTInfoModal(false);
    toggleSendNFTModal(true); // NEW: Open SendNFTModal instead of SendModal
  };
  
  const handleSendTransaction = async (itemData, amount, receiver) => {
    // This handles token transactions from SendModal
    const result = await prepareTransaction(itemData, amount, receiver, nativePrice);
    
    if (!result.success) return;
    
    // Close the send modal
    toggleSendModal(false);
    
    if (result.autoConfirm) {
      // Show auto-confirmation notification
      if (notificationRef.current) {
        notificationRef.current.showProcessing(currentChain?.explorerUrl || '');
      }
      
      // Execute the transaction without showing the confirmation modal
      const txResult = await executeTransaction(receiver, null, amount);
      
      if (txResult.success) {
        if (notificationRef.current) {
          notificationRef.current.showSuccess(txResult.txHash, currentChain?.explorerUrl || '');
        }
      } else {
        if (notificationRef.current) {
          notificationRef.current.hide();
        }
        alert(txResult.error || t('wallet.transactionFailed', 'Transaction failed'));
      }
    } else {
      // Show confirmation modal
      toggleConfirmationModal(true);
    }
  };

const handleSendNFTTransaction = async (nftData, receiver) => {
  try {
    if (!provider || !currentWallet || !nftData?.nft) {
      alert(t('wallet.errors.invalidNFTData', 'Invalid NFT data'));
      return;
    }

    // Validate receiver address
    if (!receiver || receiver.length !== 42 || !receiver.startsWith('0x')) {
      alert(t('wallet.errors.invalidAddress', 'Invalid receiver address'));
      return;
    }

    // Prepare the NFT transaction (similar to ERC20 flow)
    const result = await prepareNFTTransaction(nftData, receiver, nativePrice);
    
    if (!result.success) return;
    
    // Close the SendNFT modal
    toggleSendNFTModal(false);
    
    if (result.autoConfirm) {
      // Auto-confirm enabled: execute directly without confirmation modal
      if (notificationRef.current) {
        notificationRef.current.showProcessing(currentChain?.explorerUrl || '');
      }
      
      // Execute the NFT transaction without showing the confirmation modal
      const txResult = await executeNFTTransaction(nftData, receiver);
      
      if (txResult.success) {
        if (notificationRef.current) {
          notificationRef.current.showSuccess(txResult.txHash, currentChain?.explorerUrl || '');
        }
        // Refresh NFT data after successful transfer
        forceRefreshNFTs();
      } else {
        if (notificationRef.current) {
          notificationRef.current.hide();
        }
        alert(txResult.error || t('wallet.nftTransferFailed', 'NFT transfer failed'));
      }
    } else {
      // Auto-confirm disabled: show confirmation modal
      toggleConfirmationModal(true);
    }
  } catch (error) {
    console.error('Error in handleSendNFTTransaction:', error);
    if (notificationRef.current) {
      notificationRef.current.hide();
    }
    alert(t('wallet.nftTransferFailed', 'NFT transfer failed'));
  }
};

// Handle confirmation transaction to support both tokens and NFTs
const handleConfirmTransaction = async (gasSettings) => {
  try {
    let txResult;
    
    // Check if this is an NFT transaction or token transaction
    if (selectedNFT && selectedNFT.nft) {
      // This is an NFT transaction
      txResult = await executeNFTTransaction(selectedNFT, confirmationDetails.to, gasSettings);
    } else {
      // This is a token transaction (existing logic)
      txResult = await executeTransaction(confirmationDetails.to, gasSettings);
    }
    
    if (txResult.success) {
      setTxnHash(txResult.txHash);
      
      // Refresh data after successful transaction
      if (selectedNFT) {
        forceRefreshNFTs();
      } else {
        refreshTokens();
        refreshBalance();
      }
    } else {
      console.error("Transaction failed:", txResult.error);
      toggleConfirmationModal(false);
      alert(txResult.error || t('wallet.transactionFailed', 'Transaction failed'));
    }
  } catch (error) {
    console.error("Error in handleConfirmTransaction:", error);
    toggleConfirmationModal(false);
    alert(t('wallet.transactionFailed', 'Transaction failed'));
  }
};

  // Handle opening the portfolio chart
  const handleOpenPortfolioChart = (timeframe = '24h') => {
    const history = getCurrentWalletHistory(timeframe);
    setPortfolioChartData(history);
    setShowPortfolioChartModal(true);
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const hasWallet = userWallets && userWallets.length > 0;
  const hasLoadedWallet = hasWallet && currentWallet;
  const isLoadingOrHasWallets = isLoadingWallet || hasLoadedWallet || hasStoredWallets;
  
  // Count filtered tokens and NFTs for tab labels
  const tokenCount = filteredTokens.length;
  const nftCount = nfts.length;
  
  const renderWalletContent = () => {
     if (!hasLoadedWallet) {
      if (hasStoredWallets && !isAuthenticated) {
        return (
          <div className="no-wallet auth-required">
            <p>{t('wallet.unlockWalletToAccess', 'Please unlock your wallet to access your funds.')}</p>
          </div>
        );
      } else {
        return (
          <div className="no-wallet">
            <p>{t('wallet.noWalletFound', 'No wallet found. Please create or import one.')}</p>
          </div>
        );
      }
    } else {
      return (
        <>
          {/* Send/Receive/Scan Buttons */}
          <ActionButtons 
            onSend={handleTokenClick}
            onReceive={() => toggleReceiveModal(true)}
            onScanQR={() => setShowQRScannerModal(true)}
          />

          {/* Tab Selector for Tokens/NFTs */}
          <TabSelector
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tokenCount={tokenCount}
            nftCount={nftCount}
          />

          {/* Conditional Content Based on Active Tab */}
          {activeTab === 'tokens' ? (
            <TokenList 
              balance={balance}
              nativePrice={nativePrice}
              nativePriceChange={nativePriceChange}
              tokens={filteredTokens}
              tokenPrices={tokenPrices}
              tokenLogos={tokenLogos}
              tokenPriceChanges={tokenPriceChanges}
              onTokenClick={handleTokenClick}
              onManageTokens={() => toggleTokenManagementModal(true)}
            />
          ) : (
            <NFTList
              nfts={nfts}
              onNFTClick={handleNFTClick}
              isLoading={isNFTRefreshing}
              fromCache={nftsFromCache}
              cacheStatus={getCacheStatus()}
            />
          )}
        </>
      );
    }
  };
  
  return (
    <div className="wallet-container">
      <div
        ref={scrollContainerRef}
        className="scrollable-content"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-refresh indicator */}
        <PullToRefresh 
          pullDistance={pullDistance}
          isRefreshing={isPullRefreshing || isDataRefreshing || isNFTRefreshing}
          refreshThreshold={refreshThreshold}
        />

        {/* Header with wallet & network selection */}
        <WalletHeader 
          onOpenNetworkModal={() => toggleNetworkModal(true)}
          onOpenWalletModal={() => toggleWalletModal(true)}
        />

        {/* Portfolio Balance */}
        {isLoadingOrHasWallets && (
          <BalanceDisplay 
            totalValue={portfolioTotalValue} 
            percentChange={portfolioChangePercentage}
            onClick={() => handleOpenPortfolioChart('24h')}
          />
        )}

        {/* Wallet Content (display appropriate content based on state) */}
        {renderWalletContent()}

        <div className="bottom-spacer" />
      </div>

      {/* MODALS */}
      <NetworkModal
        isOpen={showNetworkModal}
        onClose={() => toggleNetworkModal(false)}
        onSwitchNetwork={handleSwitchNetwork}
      />

      <WalletSelectorModal
        isOpen={showWalletModal}
        onClose={() => toggleWalletModal(false)}
        wallets={userWallets}
        currentWallet={currentWallet}
        onSwitchWallet={handleSwitchWallet}
        onCreateWallet={async () => {
          try {
            // Call createWallet directly - it returns {wallet, seedPhrase}
            const result = await createWallet();
            
            // Validate the wallet was created successfully
            if (!result || !result.wallet || !result.wallet.address) {
              throw new Error(t('wallet.errors.failedToCreateWallet', 'Failed to create wallet - missing wallet data'));
            }
            
            // Add user-friendly name if not provided
            if (!result.wallet.name) {
              result.wallet.name = `Account ${userWallets.length + 1}`;
            }
            
            // Create a new wallet list with the new wallet added
            const newWalletsList = [...userWallets, result.wallet];
            
            // Update wallet list
            setUserWallets(newWalletsList);
            
            // Set this as the current wallet
            setCurrentWallet(result.wallet);
            
            // Return the wallet object itself, which is what WalletSelectorModal expects
            return result.wallet;
          } catch (error) {
            console.error("Error creating new wallet:", error);
            // Show error message to user
            throw error; // Re-throw to propagate the error to WalletSelectorModal
          }
        }}
        onImportWallet={async (privateKeyInput) => {
          try {
            // First, validate that this is actually a private key and not a seed phrase
            // Import the isPrivateKey function
            const { isPrivateKey } = require('../../utils/mnemonicUtils');
            
            // Clean input
            const cleanedInput = privateKeyInput.trim();
            
            // Check if the input is a private key
            if (!isPrivateKey(cleanedInput)) {
              throw new Error(t('wallet.errors.invalidPrivateKeyFormat'));
            }
            
            // Call importWallet (which handles both types, but we've verified it's a private key)
            const result = await importWallet(cleanedInput);
            
            // Validate we got a proper wallet back
            if (!result || !result.wallet || !result.wallet.address) {
              throw new Error(t('wallet.errors.failedToImportWallet', 'Failed to import wallet - invalid data returned'));
            }
            
            // Add a friendly name
            result.wallet.name = `Account ${userWallets.length + 1}`;
            
            // Update wallet lists
            setUserWallets((prev) => [...prev, result.wallet]);
            setCurrentWallet(result.wallet);
            
            return result.wallet;
          } catch (error) {
            console.error("Error importing wallet:", error);
            // Re-throw to propagate to the UI
            throw error;
          }
        }}
        onRemoveWallet={(w) => {
          // Don't allow removing the last wallet
          if (userWallets.length <= 1) {
            alert(t('wallet.cannotRemoveLastWallet', 'Cannot remove the last wallet. Please create a new wallet first if you want to replace this one.'));
            return;
          }
          
          setUserWallets(prev => {
            const updated = prev.filter(obj => obj.address !== w.address);
            if (currentWallet?.address === w.address) {
              setCurrentWallet(updated[0] || null);
            }
            return updated;
          });
        }}
        onUpdateWalletName={(w, newName) => {
          setUserWallets((prev) => {
            const updated = prev.map(obj =>
              obj.address === w.address ? { ...obj, name: newName } : obj
            );
            if (currentWallet?.address === w.address) {
              setCurrentWallet({ ...w, name: newName });
            }
            return updated;
          });
        }}
      />

      {/* Token SendModal */}
      <SendModal
        isOpen={showSendModal}
        onClose={() => toggleSendModal(false)}
        onSend={handleSendTransaction}
        token={selectedToken}
        tokenBalances={tokenBalancesMap}
        usdPrice={
          selectedToken && selectedToken.address
            ? tokenPrices[selectedToken.address.toLowerCase()] || 0
            : nativePrice || 0
        }
        dynamicGasPrice={dynamicGasPrice}
        gasOption={getReadableGasPrice().speed.toLowerCase()}
        customGasPrice={getReadableGasPrice().price}
      />

      {/* NEW: NFT SendModal */}
      <SendNFTModal
        isOpen={showSendNFTModal}
        onClose={() => toggleSendNFTModal(false)}
        onSend={handleSendNFTTransaction}
        nft={selectedNFT}
        dynamicGasPrice={dynamicGasPrice}
        gasOption={getReadableGasPrice().speed.toLowerCase()}
        customGasPrice={getReadableGasPrice().price}
      />
            
      <TokenInfoModal
        isOpen={showTokenInfoModal}
        onClose={() => setShowTokenInfoModal(false)}
        token={selectedToken || {}}
        tokenBalance={
          selectedToken && selectedToken.type === "native"
            ? balance || "0"
            : selectedToken && selectedToken.address
              ? tokenBalancesMap[selectedToken.address.toLowerCase()] || "0"
              : "0"
        }
        tokenPrice={
          selectedToken && selectedToken.type === "native"
            ? nativePrice || 0
            : selectedToken && selectedToken.address
              ? tokenPrices[selectedToken.address.toLowerCase()] || null
              : null
        }
        nativePrice={nativePrice}
        onSend={handleSendFromInfo}
        isNative={selectedToken && selectedToken.type === "native"}
      />

      {/* NFTInfoModal with onSend prop */}
      <NFTInfoModal
        isOpen={showNFTInfoModal}
        onClose={() => setShowNFTInfoModal(false)}
        nft={selectedNFT}
        onSend={handleSendFromNFTInfo} // Opens SendNFTModal
      />

      <ReceiveModal
        isOpen={showReceiveModal}
        onClose={() => toggleReceiveModal(false)}
        address={currentWallet?.address || ''}
      />

      <TokenManagementModal
        isOpen={showTokenManagementModal}
        onClose={() => toggleTokenManagementModal(false)}
        tokens={[
          ...tokens, 
          ...(customTokens[currentChain?.key] || [])
        ].map(t => ({
          ...t,
          hidden: t.token && t.token.address && currentChain?.key ? 
            (currentChainHiddenTokens.includes(t.token.address.toLowerCase())) : false
        }))}
        onToggleVisibility={handleToggleTokenVisibility}
        onAddCustomToken={handleAddCustomToken}
        onRemoveCustomToken={handleRemoveCustomToken}
      />

      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => toggleConfirmationModal(false)}
        onConfirm={handleConfirmTransaction}
        transactionDetails={confirmationDetails}
        txnHash={txnHash}
        explorerUrl={currentChain?.explorerUrl || ''}
        currentGasOption={getReadableGasPrice().speed.toLowerCase()}
        currentGasPrice={getReadableGasPrice().price}
        dynamicGasPrice={dynamicGasPrice}
        onGasSettingChange={handleGasSettingChange}
      />

      {/* Portfolio Chart Modal */}
      <PortfolioChartModal
        isOpen={showPortfolioChartModal}
        onClose={() => setShowPortfolioChartModal(false)}
        portfolioData={portfolioChartData}
        currentValue={portfolioTotalValue}
      />

      <TransactionNotificationWrapper
        ref={notificationRef}
      />
      
      {/* Connection Success Notification */}
      <ConnectionSuccessNotificationWrapper
        ref={connectionSuccessRef}
      />
      
      {/* QR Scanner Modal */}
      <QRScannerModal
        isOpen={showQRScannerModal}
        onClose={() => setShowQRScannerModal(false)}
      />
      <RequestModalController origin="wallet" />
    </div>
  );
};

export default Wallet;